import { CommonModule } from '@angular/common';
import { Component, ElementRef, inject, NgZone, Renderer2, ViewChild, ViewChildren, QueryList, AfterViewInit, OnDestroy } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRouteSnapshot, RouterLink, RouterLinkActive } from '@angular/router';
import { Router, NavigationEnd } from '@angular/router';
import { BreadcrumbService } from '../shared/breadcrumb.service';
import { IsFhirReferencePipe } from "../fhirIdCheck.pipe";
import { ReferenceDisplayDirective } from '../shared/reference-display.directive';
import { filter, fromEvent, merge, Subject, takeUntil, debounceTime } from 'rxjs';

@Component({
  selector: 'app-breadcrumb-navigation',
  imports: [RouterLink, RouterLinkActive, CommonModule, MatIconModule, IsFhirReferencePipe, ReferenceDisplayDirective],
  templateUrl: './breadcrumb-navigation.component.html',
  styleUrl: './breadcrumb-navigation.component.scss'
})
export class BreadcrumbNavigationComponent implements AfterViewInit, OnDestroy {
  breadCrumbService = inject(BreadcrumbService);
  private router = inject(Router);
  private renderer = inject(Renderer2);
  private zone = inject(NgZone);
  private destroyed$ = new Subject<void>();

  // Collect all "#breadcrumbItem" spans
  @ViewChildren('breadcrumbItem', { read: ElementRef })
  breadcrumbItems!: QueryList<ElementRef<HTMLElement>>;

  // Optional: the nav wrapper to compute available width
  @ViewChild('breadcrumbNav', { read: ElementRef })
  breadcrumbNav?: ElementRef<HTMLElement>;

  ngAfterViewInit(): void {
    // Re-apply after view children change (route updates)
    this.breadcrumbItems.changes
      .pipe(takeUntil(this.destroyed$), debounceTime(0))
      .subscribe(() => this.applyCrumbSizing());

    // Apply after navigation end + resize
    this.router.events
      .pipe(
        takeUntil(this.destroyed$),
        filter((e): e is NavigationEnd => e instanceof NavigationEnd)
      )
      .subscribe(() => {
        // allow DOM to render new crumbs
        queueMicrotask(() => this.applyCrumbSizing());
      });

    // Resize handling outside angular for less churn
    this.zone.runOutsideAngular(() => {
      merge(fromEvent(window, 'resize'))
        .pipe(takeUntil(this.destroyed$), debounceTime(100))
        .subscribe(() => this.applyCrumbSizing());
    });

    // Initial
    this.applyCrumbSizing();
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  private applyCrumbSizing(): void {
    const items = this.breadcrumbItems?.toArray?.() ?? [];
    if (!items.length) return;

    // Determine available width: prefer nav container if present
    const containerEl =
      (document.querySelector('.breadcrumb-navigation') as HTMLElement | null) ||
      this.breadcrumbNav?.nativeElement ||
      items[0].nativeElement.parentElement;

    const containerWidth = containerEl?.getBoundingClientRect().width ?? window.innerWidth;

    // Heuristic: reserve some width for icons/separators/padding
    const reserved = 140; // tweak if needed
    const available = Math.max(240, containerWidth - reserved);

    const count = items.length;

    // Per-item max width: clamp so single crumbs don't get huge, and many crumbs still show something.
    const minPerItem = 72;
    const maxPerItem = 260;
    const perItem = Math.max(minPerItem, Math.min(maxPerItem, Math.floor(available / count)));

    for (const ref of items) {
      const el = ref.nativeElement;

      // Apply ellipsis styles
      this.renderer.setStyle(el, 'max-width', `${perItem}px`);
      this.renderer.setStyle(el, 'overflow', 'hidden');
      this.renderer.setStyle(el, 'text-overflow', 'ellipsis');
      this.renderer.setStyle(el, 'white-space', 'nowrap');
      this.renderer.setStyle(el, 'display', 'inline-block');

      // Title = full visible text (after ReferenceDisplayDirective updates)
      const text = (el.textContent ?? '').trim();
      if (text) {
        this.renderer.setAttribute(el, 'title', text);
      }
    }
  }

  buildBreadcrumb(currentRoute: ActivatedRouteSnapshot[]) {

    // console.log(this.breadCrumbBuild);

    // if(this.profileEditComponent.showNotice){
    //   this.allNotices.push(this.profileEditComponent.profileEditNotice);
    // }

    // this.noticesWrapper.createEmbeddedView(this.profileEditComponent.profileEditNotice);

  }
}
